/**
 * Meeting Detail → Client questions (#4211, Working Session 2026-10-30:
 * "structured post-meeting client feedback, A/B/C/D/E options"): the wire
 * shapes of `meeting-inquiry-drafts`
 * (packages/api/src/services/read-ai/meeting-inquiries/meeting-inquiry-drafts.ts),
 * the editor a person fills in, and the `client-inquiries` filing it becomes.
 * Pure, so every rule here is pinned by meetingInquiry.types.test.ts.
 *
 * THE RULE: HQ may DRAFT the options; a person edits them and files them, and
 * nothing reaches the client until a person sends it from the Inquiry Queue.
 */

export interface MeetingQuestionRow {
  text: string;
  /** The meeting's analysis says the meeting answered it. */
  answeredInMeeting: boolean;
  /** The client question that already asks it, or null. */
  inquiry: { id: number; stage: string } | null;
}

export interface FiledFromMeeting {
  id: number;
  question: string;
  stage: string;
  optionCount: number;
  filedAt: string;
  filedBy: string | null;
}

export interface MeetingQuestions {
  /** The meeting's id: the ServiceStore key. */
  id: number;
  meetingId: number;
  meetingTitle: string | null;
  when: string | null;
  clientId: string | null;
  clientName: string | null;
  clientCode: string | null;
  /** Why no client question can be drafted from this meeting; null when it can. */
  blocked: string | null;
  questions: MeetingQuestionRow[];
  filed: FiledFromMeeting[];
  /**
   * Whether an AI draft can be asked for: false only when HQ has no AI provider
   * for the draft. A private meeting is drafted from like any other (#4028:
   * private is who sees a meeting, never how it is processed).
   */
  ai: { configured: boolean };
}

export interface DraftOption {
  title: string;
  text: string;
  implication: string | null;
}

export interface InquiryDraft {
  kind: "design" | "data";
  title: string;
  intro: string;
  options: DraftOption[];
  recommendation: string | null;
}

export interface InquiryDraftResult {
  meetingId: number;
  question: string;
  draft: InquiryDraft;
  otherClients: string[];
}

/** HQ files 2 to 5 options, lettered A to E (the API's client-inquiry-record.ts). */
export const OPTION_LETTERS = ["A", "B", "C", "D", "E"] as const;
export const MIN_OPTIONS = 2;
export const MAX_OPTIONS = OPTION_LETTERS.length;

export interface EditorOption {
  title: string;
  /** What the client reads for this option. */
  text: string;
  implication: string;
}

/** The form a person fills in before filing. */
export interface InquiryEditor {
  /** How HQ files it (and dedupes it): the question itself. */
  question: string;
  kind: "design" | "data";
  /** The heading the client reads. */
  title: string;
  /** What the client reads first. */
  intro: string;
  options: EditorOption[];
  recommendation: string;
  /** Internal only: never published. */
  note: string;
}

const blankOption = (): EditorOption => ({ title: "", text: "", implication: "" });

/** A fresh form for a question: two empty options, the question as the client's heading. */
export const blankEditor = (question: string): InquiryEditor => ({
  question,
  kind: "design",
  title: question,
  intro: "",
  options: [blankOption(), blankOption()],
  recommendation: "",
  note: "",
});

/** The form, filled from an AI draft. The question and the person's own note are kept. */
export const editorFromDraft = (e: InquiryEditor, draft: InquiryDraft): InquiryEditor => ({
  ...e,
  kind: draft.kind,
  title: draft.title || e.title,
  intro: draft.intro,
  options: draft.options.slice(0, MAX_OPTIONS).map((o) => ({ title: o.title, text: o.text, implication: o.implication ?? "" })),
  recommendation: draft.recommendation ?? "",
});

/** Why the question cannot be filed yet, in one sentence; null when it can. */
export const fileBlocker = (e: InquiryEditor | null): string | null => {
  if (!e) return "Choose a question first.";
  if (!e.question.trim()) return "Write the question.";
  if (e.options.length < MIN_OPTIONS || e.options.length > MAX_OPTIONS)
    return `A client question has ${MIN_OPTIONS} to ${MAX_OPTIONS} options.`;
  const untitled = e.options.findIndex((o) => !o.title.trim());
  if (untitled >= 0) return `Option ${OPTION_LETTERS[untitled]} needs a title.`;
  const titles = e.options.map((o) => o.title.trim().toLowerCase());
  if (new Set(titles).size !== titles.length) return "Two options have the same title.";
  return null;
};

/**
 * What the client cannot read yet: the API files a question without these, and
 * release, the PDF and the email each refuse until they are written. Said, not
 * enforced, so a person can file now and finish the words in the Inquiry Queue.
 */
export const clientTextStillToWrite = (e: InquiryEditor): string[] => {
  const out: string[] = [];
  if (!e.title.trim()) out.push("the title");
  if (!e.intro.trim()) out.push("the intro");
  e.options.forEach((o, i) => {
    if (!o.text.trim()) out.push(`option ${OPTION_LETTERS[i]}'s text`);
  });
  return out;
};

const opt = (s: string): string | null => (s.trim() ? s.trim() : null);

/**
 * The `client-inquiries.create` body: the person's question with its options
 * lettered in order, the words the client will read, an internal note saying
 * where it came from, and an `hq` origin naming the meeting (provenance only:
 * it holds no work).
 */
export const filingBody = (
  e: InquiryEditor,
  m: Pick<MeetingQuestions, "meetingId" | "meetingTitle" | "when" | "clientCode">,
  aiDrafted: boolean,
): Record<string, unknown> => {
  const ids = e.options.map((_, i) => OPTION_LETTERS[i].toLowerCase());
  const where = [m.meetingTitle ? `"${m.meetingTitle}"` : null, m.when].filter(Boolean).join(", ");
  const context = [
    `Drafted in HQ from meeting ${m.meetingId}${where ? ` (${where})` : ""}${aiDrafted ? "; options first drafted by AI and edited by a person" : ""}.`,
    opt(e.note),
  ]
    .filter(Boolean)
    .join("\n");
  return {
    clientCode: m.clientCode,
    question: e.question.trim(),
    options: e.options.map((o, i) => ({
      id: ids[i],
      title: o.title.trim(),
      text: opt(o.text),
      implication: opt(o.implication),
    })),
    clientText: {
      kind: e.kind,
      title: e.title.trim(),
      intro: e.intro.trim(),
      optionText: Object.fromEntries(
        e.options.map((o, i) => [ids[i], o.text.trim()] as const).filter(([, t]) => t !== ""),
      ),
      recommendation: opt(e.recommendation),
    },
    internalContext: context,
    origin: { kind: "hq", meetingId: m.meetingId },
  };
};
